/**
 * Cart Samples — pick N samples (+1 random surprise), added to the cart as
 * free line items flagged with the `_sample` property.
 */
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartSamplesComponent extends DialogComponent {
  #selected = new Set();
  #busy = false;

  get max() {
    return parseInt(this.dataset.max, 10) || 2;
  }

  get surpriseIds() {
    return (this.dataset.surprise || '')
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter(Boolean);
  }

  async showDialog() {
    await super.showDialog();
    // Pre-select samples already in the cart (modify flow).
    await this.#syncFromCart();
  }

  toggleSample(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn) return;
    const id = parseInt(btn.dataset.variantId, 10);
    if (this.#selected.has(id)) {
      this.#selected.delete(id);
      btn.classList.remove('is-selected');
    } else if (this.#selected.size < this.max) {
      this.#selected.add(id);
      btn.classList.add('is-selected');
    }
    this.#refresh();
  }

  async validateSamples() {
    if (this.#busy) return;
    this.#busy = true;

    const sectionId = this.#sectionId();

    // Snapshot the selection, then close immediately; the drawer reconciles in the background.
    const items = [...this.#selected].map((id) => ({
      id,
      quantity: 1,
      properties: { _sample: 'true' },
    }));
    // A surprise comes only when at least one sample is chosen.
    if (items.length) {
      const pool = this.surpriseIds.filter((id) => !this.#selected.has(id));
      if (pool.length) {
        const surprise = pool[Math.floor(Math.random() * pool.length)];
        items.push({ id: surprise, quantity: 1, properties: { _sample: 'true', _surprise: 'true' } });
      }
    }
    // Optimistic drawer update, then close instantly.
    this.#reflectSamplesInCart(items.length > 0);
    this.closeDialog();

    try {
      // Remove the previous samples in a single batched update (not one call per line).
      const removeUpdates = await this.#sampleRemovalUpdates();
      const needRemoval = Object.keys(removeUpdates).length > 0;

      let data;
      if (items.length) {
        // Ask for the section HTML in the add call only when there's no removal to follow.
        const addBody = JSON.stringify(
          needRemoval
            ? { items }
            : { items, sections: sectionId, sections_url: window.location.pathname }
        );
        const addRes = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
        data = await addRes.json();
        if (needRemoval) data = await this.#applyUpdates(removeUpdates, sectionId);
      } else {
        // Nothing selected → just clear the existing samples (and render).
        data = await this.#applyUpdates(removeUpdates, sectionId);
      }

      document.dispatchEvent(new CartAddEvent(data, sectionId, { sections: data.sections }));
    } catch (err) {
      console.error('Samples error:', err);
    } finally {
      this.#busy = false;
    }
  }

  /** Clear every sample from the cart in one click (from the cart row). */
  async removeSamples() {
    if (this.#busy) return;
    this.#busy = true;

    const sectionId = this.#sectionId();
    this.#selected.clear();

    // Optimistic removal feedback; the morph confirms it.
    this.#reflectSamplesInCart(false);

    try {
      // One batched update removes every sample line AND renders the section.
      const updates = await this.#sampleRemovalUpdates();
      const data = await this.#applyUpdates(updates, sectionId);
      document.dispatchEvent(new CartAddEvent(data, sectionId, { sections: data.sections }));
    } catch (err) {
      console.error('Sample remove error:', err);
    } finally {
      this.#busy = false;
    }
  }

  #sectionId() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    return cartDrawer ? cartDrawer.dataset.sectionId : null;
  }

  /** Instantly mirror the "samples in cart" state in the drawer (check + remove link). */
  #reflectSamplesInCart(present) {
    document.querySelectorAll('[data-sample-check]').forEach((el) => el.classList.toggle('is-checked', present));
    document.querySelectorAll('[data-remove-samples]').forEach((el) => { el.hidden = !present; });
  }

  /** Fetch the cart once and build a { lineKey: 0 } map for every sample line. */
  async #sampleRemovalUpdates() {
    const updates = {};
    try {
      const cart = await this.#fetchCart();
      cart.items.forEach((i) => {
        if (i.properties && i.properties._sample) updates[i.key] = 0;
      });
    } catch { /* ignore */ }
    return updates;
  }

  /** Apply line-quantity updates and render the cart-drawer section in one call. */
  async #applyUpdates(updates, sectionId) {
    const body = JSON.stringify({ updates, sections: sectionId, sections_url: window.location.pathname });
    const res = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    return res.json();
  }

  async #syncFromCart() {
    this.#selected.clear();
    // Only pre-select samples still offered by the picker (ignore stale ones).
    const optionIds = new Set(
      [...this.querySelectorAll('[data-variant-id]')].map((btn) => parseInt(btn.dataset.variantId, 10))
    );
    try {
      const cart = await this.#fetchCart();
      const chosen = cart.items.filter((i) => i.properties && i.properties._sample && !i.properties._surprise);
      chosen.forEach((i) => {
        if (optionIds.has(i.variant_id)) this.#selected.add(i.variant_id);
      });
    } catch { /* ignore */ }
    this.querySelectorAll('[data-variant-id]').forEach((btn) => {
      const id = parseInt(btn.dataset.variantId, 10);
      btn.classList.toggle('is-selected', this.#selected.has(id));
    });
    this.#refresh();
  }

  #refresh() {
    const count = this.#selected.size;
    if (this.refs.sampleCount) this.refs.sampleCount.textContent = count;

    // Grey out the non-selected options once the max is reached.
    if (this.refs.list) this.refs.list.classList.toggle('is-max', count >= this.max);

    // Validate stays enabled at 0 (it clears the samples); swap its label.
    const btn = this.refs.validateBtn;
    if (btn) {
      btn.textContent = count === 0 ? btn.dataset.emptyLabel || btn.textContent : btn.dataset.label || btn.textContent;
    }
  }

  async #fetchCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }
}

if (!customElements.get('cart-samples-component')) {
  customElements.define('cart-samples-component', CartSamplesComponent);
}
